<template>
  <div class="d-inline-flex">
    <div
      v-if="showDesktopFilters"
      class="d-inline-flex"
    >
      <template v-if="meta.openings.enabled && (meta.openings.public || $role == 'specialist')">
        <FilterMenu
          @clear="modelValue.vacancy = null"
          @close="emitChange()"
          :actions="true"
          :active="modelValue.vacancy != null"
          tid="search_availability_filter"
          title="Availability"
        >
          <template #card>
            <v-checkbox
              v-model="modelValue.vacancy"
              class="mt-0 mb-0"
              color="primary"
              data-testId="availability-checkbox"
              false-icon="check_box_outline_blank"
              true-icon="check_box"
              hide-details
            >
              <template #label>
                <span
                  class="c-black"
                  data-testid="availability-checkmark"
                >
                  {{ $bct('Only show providers that have reported availability') }}
                </span>
              </template>
            </v-checkbox>
          </template>
        </FilterMenu>
      </template>

      <FilterMenu
        @clear="modelValue.distance = null"
        @close="emitChange()"
        :actions="true"
        :active="modelValue.distance != null"
        :title="$bct('Distance')"
        aria-label="menuitem"
        min-width="375px"
        tid="search_distance_filter"
      >
        <template #card>
          <v-row>
            <v-col
              class="fw-600"
              cols="12"
            >
              {{ $bct('Only show me options within a specific distance') }}
            </v-col>

            <v-col>
              <!-- eslint-disable-next-line prefer-standard-components/no-vuetify-inputs -- TODO migrate to LabeledSelect -->
              <v-select
                v-model="modelValue.distance"
                :items="mileageRange()"
                :label="$bct('Distance in miles')"
                class="c-black mt-0"
                color="primary"
                hide-details
              />
            </v-col>
          </v-row>
        </template>
      </FilterMenu>

      <template v-if="schemaProperties.quality_rating_score.filterable">
        <FilterMenu
          @clear="modelValue.minimum_quality_rating = null"
          @close="emitChange()"
          :actions="true"
          :active="modelValue.minimum_quality_rating != null"
          :title="schemaProperties.quality_rating_score.alias"
          min-width="250px"
          tid="search_quality_filter"
        >
          <template #card>
            <template v-if="store.state.site.ratings.description || store.state.site.ratings.link">
              <div class="d-flex align-center ga-2">
                <span>{{ $bct('Learn more') }}</span>

                <TooltipIcon
                  v-if="store.state.site.ratings.description"
                  :text="store.state.site.ratings.description"
                  color="grey"
                  icon="info"
                  location="bottom"
                />

                <a
                  v-if="store.state.site.ratings.link"
                  :href="store.state.site.ratings.link"
                  class="c-primary fs-15 ms-1 underlined"
                  target="_blank"
                >
                  <v-icon
                    icon="launch"
                    size="24"
                  />
                </a>
              </div>

              <v-divider class="my-3" />
            </template>

            <v-radio-group
              v-model="modelValue.minimum_quality_rating"
              class="mt-0"
              hide-details
            >
              <div
                v-for="(level, index) in store.state.site.ratings.text.slice().reverse()"
                :key="index"
              >
                <RadioWithContext
                  :index="index"
                  :item="level"
                  :suffix="
                    store.state.site.ratings.text.length - index <
                    store.state.site.ratings.text.length
                      ? $bct('or higher')
                      : null
                  "
                  :value="store.state.site.ratings.max - index"
                />

                <v-divider
                  v-if="level.subtitle"
                  class="my-3"
                />
              </div>
            </v-radio-group>
          </template>
        </FilterMenu>
      </template>

      <template v-if="schemaProperties.county.filterable && !overflowMode">
        <FilterMenu
          @clear="modelValue.county = []"
          @close="emitChange()"
          :actions="true"
          :active="modelValue.county.length > 0"
          :title="schemaProperties.county.alias || 'County'"
          aria-label="menuitem"
          min-width="375px"
          tid="search_county_filter"
        >
          <template #card>
            <!-- eslint-disable-next-line prefer-standard-components/no-vuetify-inputs -- TODO migrate to LabeledSelect -->
            <v-select
              v-model="modelValue.county"
              :items="schemaProperties.county.enum"
              :label="$bct('County')"
              class="c-black mt-0"
              color="primary"
              hide-details
              multiple
            />
          </template>
        </FilterMenu>
      </template>

      <template v-if="schemaProperties.program_types.filterable">
        <FilterMenu
          @clear="modelValue.program_types = []"
          @close="emitChange()"
          :actions="true"
          :active="modelValue.program_types.length > 0"
          :title="schemaProperties.program_types.alias || 'Program types'"
          aria-label="menuitem"
          min-width="375px"
          tid="search_program_types_filter"
          paddingless
        >
          <template #card>
            <v-list
              v-model:selected="modelValue.program_types"
              :item-value="(item) => item"
              :items="schemaProperties.program_types.items.enum"
              select-strategy="independent"
            >
              <template #prepend="{ isSelected }">
                <v-checkbox-btn
                  :model-value="isSelected"
                  false-icon="check_box_outline_blank"
                  tabindex="-1"
                  true-icon="check_box"
                />
              </template>
              <template #title="{ item }">
                {{ capitalize($bct(item)) }}
              </template>
            </v-list>
          </template>
        </FilterMenu>
      </template>

      <template v-if="schemaProperties.program_season.filterable && !overflowMode">
        <FilterMenu
          @clear="modelValue.program_season = null"
          @close="emitChange()"
          :actions="true"
          :active="modelValue.program_season != null"
          :title="schemaProperties.program_season.alias || 'Season'"
          aria-label="menuitem"
          min-width="375px"
          tid="search_program_season_filter"
        >
          <template #card>
            <v-radio-group
              v-model="modelValue.program_season"
              class="mt-0"
              hide-details
            >
              <v-radio
                v-for="type in schemaProperties.program_season.enum"
                :key="type"
                :label="capitalize($bct(type))"
                :value="type"
                class="c-black mt-0"
                color="primary"
                hide-details
              />
            </v-radio-group>
          </template>
        </FilterMenu>
      </template>

      <template v-if="schemaProperties.license_type.filterable">
        <FilterMenu
          @clear="modelValue.license_type = []"
          @close="emitChange()"
          :actions="true"
          :active="modelValue.license_type.length > 0"
          :title="schemaProperties.license_type.alias"
          aria-label="menuitem"
          min-width="375px"
          tid="search_license_types_filter"
          paddingless
        >
          <template #card>
            <v-list
              v-model:selected="modelValue.license_type"
              :item-value="(item) => item"
              :items="schemaProperties.license_type.enum"
              select-strategy="independent"
            >
              <template #prepend="{ isSelected }">
                <v-checkbox-btn
                  :model-value="isSelected"
                  false-icon="check_box_outline_blank"
                  tabindex="-1"
                  true-icon="check_box"
                />
              </template>
              <template #title="{ item }">
                {{ capitalize($bct(item)) }}
              </template>
            </v-list>
          </template>
        </FilterMenu>
      </template>

      <template v-if="schemaProperties.categories.filterable && !overflowMode">
        <FilterMenu
          @clear="modelValue.categories = []"
          @close="emitChange()"
          :actions="true"
          :active="modelValue.categories.length > 0"
          :title="schemaProperties.categories.alias"
          aria-label="menuitem"
          min-width="375px"
          tid="search_categories_filter"
          paddingless
        >
          <template #card>
            <template v-if="schemaProperties.categories.items?.enum?.length < 20">
              <v-list
                v-model:selected="modelValue.categories"
                :item-value="(item) => item"
                :items="schemaProperties.categories.items.enum"
                select-strategy="independent"
              >
                <template #prepend="{ isSelected }">
                  <v-checkbox-btn
                    :model-value="isSelected"
                    false-icon="check_box_outline_blank"
                    tabindex="-1"
                    true-icon="check_box"
                  />
                </template>
                <template #title="{ item }">
                  {{ capitalize($bct(item)) }}
                </template>
              </v-list>
            </template>

            <div
              v-else
              class="pa-3"
            >
              <v-autocomplete
                v-model="modelValue.categories"
                :item-title="(item) => capitalize($bct(item))"
                :item-value="(item) => item"
                :items="schemaProperties.categories.items.enum || []"
                :label="$bct('Select all that apply')"
                variant="filled"
                chips
                closable-chips
                hide-details
                multiple
              />
            </div>
          </template>
        </FilterMenu>
      </template>

      <template v-if="schemaProperties.hours.filterable">
        <FilterMenu
          @clear="clearHours()"
          @close="emitChange()"
          :actions="true"
          :active="modelValue.week_days.length > 0"
          :save-disabled="modelValue.week_days.length == 0"
          aria-label="menuitem"
          min-width="400"
          tid="search_hours_filter"
          title="Hours"
        >
          <template #card>
            <v-row>
              <LabeledOpenClose
                v-model="hoursRange"
                close-title="Pickup time"
                open-title="Drop-off time"
              />
            </v-row>

            <div class="c-gray fs-15 mt-2 mb-3 mxw-400">
              {{
                $bct(
                  "We'll show you results that offer drop-off at the time requested or earlier, and pick-up at the time requested or later",
                )
              }}
            </div>

            <v-row dense>
              <v-col
                class="fw-600"
                cols="12"
              >
                {{ $bct('Days of the week') }}
              </v-col>

              <v-col cols="12">
                <v-checkbox
                  v-model="modelValue.week_days"
                  :label="$bct('Monday')"
                  class="mt-0"
                  density="compact"
                  value="0"
                  hide-details
                />

                <v-checkbox
                  v-model="modelValue.week_days"
                  :label="$bct('Tuesday')"
                  class="mt-2"
                  density="compact"
                  value="1"
                  hide-details
                />

                <v-checkbox
                  v-model="modelValue.week_days"
                  :label="$bct('Wednesday')"
                  class="mt-2"
                  density="compact"
                  value="2"
                  hide-details
                />

                <v-checkbox
                  v-model="modelValue.week_days"
                  :label="$bct('Thursday')"
                  class="mt-2"
                  density="compact"
                  value="3"
                  hide-details
                />

                <v-checkbox
                  v-model="modelValue.week_days"
                  :label="$bct('Friday')"
                  class="mt-2"
                  density="compact"
                  value="4"
                  hide-details
                />

                <v-checkbox
                  v-model="modelValue.week_days"
                  :label="$bct('Saturday')"
                  class="mt-2"
                  density="compact"
                  value="5"
                  hide-details
                />

                <v-checkbox
                  v-model="modelValue.week_days"
                  :label="$bct('Sunday')"
                  class="mt-2"
                  density="compact"
                  value="6"
                  hide-details
                />
              </v-col>
            </v-row>
          </template>
        </FilterMenu>
      </template>

      <template v-if="store.state.pages.Search.features.enable_more_filters">
        <v-btn
          @click="openMoreFiltersDialog()"
          :class="moreFiltersButtonClass"
          class="focus-visible"
          data-tid="search_more_filter"
          variant="outlined"
          rounded
        >
          <span
            class="bc-body c-black fw-500 fs-14"
            data-testid="more-filters"
          >
            {{ $bct('More filters') }}
          </span>
        </v-btn>
      </template>
    </div>

    <v-btn
      v-if="!showDesktopFilters"
      @click="allFiltersDialogIsVisible = true"
      class="bg-white me-2 bc-very-light-gray"
      data-testid="search_all_filter"
      data-tid="search_all_filter"
      variant="outlined"
      rounded
    >
      <v-icon
        class="me-1 fs-20 c-primary fw-500"
        icon="filter_list"
      />

      <span class="bc-body c-black fw-600">
        {{ $bct('Filters') }}
      </span>
    </v-btn>

    <v-dialog
      v-model="moreFiltersDialogIsVisible"
      width="600px"
      scrollable
    >
      <v-card
        border
        flat
        tile
      >
        <v-card-title class="mb-n-3 pb-0">
          {{ $bct('More filters') }}
        </v-card-title>

        <v-card-text class="pt-0 pb-6">
          <template v-if="overflowMode && schemaProperties.county.filterable">
            <v-row dense>
              <v-col class="c-black fs-16 fw-600">
                {{ $bct(schemaProperties.county.alias || 'County') }}
              </v-col>
            </v-row>
            <v-row
              class="mb-4"
              dense
            >
              <v-col>
                <!-- eslint-disable-next-line prefer-standard-components/no-vuetify-inputs -- TODO migrate to LabeledSelect -->
                <v-select
                  v-model="modelValue.county"
                  :items="schemaProperties.county.enum"
                  :label="$bct('County')"
                  class="c-black mt-0"
                  color="primary"
                  hide-details
                  multiple
                />
              </v-col>
            </v-row>
            <v-divider class="my-6" />
          </template>

          <template v-if="overflowMode && schemaProperties.program_season.filterable">
            <v-row dense>
              <v-col class="c-black fs-16 fw-600">
                {{ $bct(schemaProperties.program_season.alias || 'Season') }}
              </v-col>
            </v-row>
            <v-row
              class="mb-4"
              dense
            >
              <v-col>
                <v-radio-group
                  v-model="modelValue.program_season"
                  class="mt-0"
                  hide-details
                >
                  <v-radio
                    v-for="type in schemaProperties.program_season.enum"
                    :key="type"
                    :label="capitalize($bct(type))"
                    :value="type"
                    class="c-black mt-0"
                    color="primary"
                    hide-details
                  />
                </v-radio-group>
              </v-col>
            </v-row>
            <v-divider class="my-6" />
          </template>

          <template v-if="overflowMode && schemaProperties.categories.filterable">
            <v-row dense>
              <v-col class="c-black fs-16 fw-600">
                {{ $bct(schemaProperties.categories.alias || 'Categories') }}
              </v-col>
            </v-row>
            <v-row
              class="mb-4"
              dense
            >
              <v-col>
                <template v-if="schemaProperties.categories.items?.enum?.length < 20">
                  <v-checkbox
                    v-for="type in schemaProperties.categories.items.enum"
                    v-model="modelValue.categories"
                    :key="type"
                    :label="capitalize($bct(type))"
                    :value="type"
                    class="c-black mt-0"
                    color="primary"
                    hide-details
                    multiple
                  />
                </template>
                <template v-else>
                  <v-autocomplete
                    v-model="modelValue.categories"
                    :items="schemaProperties.categories.items.enum"
                    :label="$bct('Select all that apply')"
                    variant="filled"
                    chips
                    closable-chips
                    hide-details
                    multiple
                  />
                </template>
              </v-col>
            </v-row>
            <v-divider class="my-6" />
          </template>

          <SearchMoreFilters :value="modelValue" />
        </v-card-text>

        <v-divider />

        <v-card-actions class="pt-2">
          <v-btn
            @click="clearMoreFilters()"
            color="primary"
            data-testid="clear-filters"
            variant="text"
          >
            {{ $bct('Clear filters') }}
          </v-btn>

          <v-spacer />

          <v-btn
            @click="emitChange()"
            color="primary"
            data-testid="apply-filter"
          >
            {{ $bct('Apply') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="allFiltersDialogIsVisible"
      fullscreen
      scrollable
    >
      <v-card>
        <v-card-title>
          <v-row class="d-flex align-center">
            <v-col class="d-flex justify-start">
              <span>{{ $bct('Filters') }}</span>
            </v-col>

            <v-col class="d-flex justify-end">
              <v-btn
                @click="allFiltersDialogIsVisible = false"
                :aria-label="$bct('Close filter')"
                icon="close"
                variant="text"
              />
            </v-col>
          </v-row>
        </v-card-title>

        <v-divider class="mb-6" />

        <v-card-text class="pt-0 pb-6">
          <template v-if="meta.openings.enabled && (meta.openings.public || $role == 'specialist')">
            <v-row dense>
              <v-col class="c-black fs-16 fw-600">
                {{ $bct('Availability') }}
              </v-col>
            </v-row>

            <v-row
              class="mb-4"
              dense
            >
              <v-col>
                <v-checkbox
                  v-model="modelValue.vacancy"
                  class="mt-0 mb-0"
                  color="primary"
                  false-icon="check_box_outline_blank"
                  true-icon="check_box"
                  hide-details
                >
                  <template #label>
                    <span
                      class="c-black"
                      data-testid="availability-checkmark"
                    >
                      {{ $bct('Only show providers that have reported availability') }}
                    </span>
                  </template>
                </v-checkbox>
              </v-col>
            </v-row>

            <v-divider class="my-6" />
          </template>

          <v-row dense>
            <v-col class="c-black fs-16 fw-600">
              {{ $bct('Distance') }}
            </v-col>
          </v-row>

          <v-row
            class="mb-4"
            dense
          >
            <v-col>
              <div class="c-black mb-2">
                {{ $bct('Only show me options within a specific distance') }}
              </div>
              <!-- eslint-disable-next-line prefer-standard-components/no-vuetify-inputs -- TODO migrate to LabeledSelect -->
              <v-select
                v-model="modelValue.distance"
                :items="mileageRange()"
                :label="$bct('Distance in miles')"
                class="c-black mt-0"
                color="primary"
                hide-details
              />
            </v-col>
          </v-row>

          <v-divider class="my-6" />

          <template v-if="schemaProperties.hours.filterable">
            <v-row>
              <LabeledOpenClose
                v-model="hoursRange"
                close-title="Pickup time"
                open-title="Drop-off time"
              />
            </v-row>

            <div class="c-gray fs-15 mt-2 mb-3 mxw-400">
              {{
                $bct(
                  "We'll show you results that offer drop-off at the time requested or earlier, and pick-up at the time requested or later",
                )
              }}
            </div>

            <v-row dense>
              <v-col
                class="fw-600"
                cols="12"
              >
                {{ $bct('Days of the week') }}
              </v-col>

              <v-col cols="12">
                <v-checkbox
                  v-model="modelValue.week_days"
                  :label="$bct('Monday')"
                  class="mt-0"
                  density="compact"
                  value="0"
                  hide-details
                />

                <v-checkbox
                  v-model="modelValue.week_days"
                  :label="$bct('Tuesday')"
                  class="mt-2"
                  density="compact"
                  value="1"
                  hide-details
                />

                <v-checkbox
                  v-model="modelValue.week_days"
                  :label="$bct('Wednesday')"
                  class="mt-2"
                  density="compact"
                  value="2"
                  hide-details
                />

                <v-checkbox
                  v-model="modelValue.week_days"
                  :label="$bct('Thursday')"
                  class="mt-2"
                  density="compact"
                  value="3"
                  hide-details
                />

                <v-checkbox
                  v-model="modelValue.week_days"
                  :label="$bct('Friday')"
                  class="mt-2"
                  density="compact"
                  value="4"
                  hide-details
                />
                <v-checkbox
                  v-model="modelValue.week_days"
                  :label="$bct('Saturday')"
                  class="mt-2"
                  density="compact"
                  value="5"
                  hide-details
                />

                <v-checkbox
                  v-model="modelValue.week_days"
                  :label="$bct('Sunday')"
                  class="mt-2"
                  density="compact"
                  value="6"
                  hide-details
                />
              </v-col>
            </v-row>
            <v-divider class="my-6" />
          </template>

          <template v-if="schemaProperties.program_types.filterable">
            <v-row dense>
              <v-col class="c-black fs-16 fw-600">
                {{ $bct(schemaProperties.program_types.alias || 'Program types') }}
              </v-col>
            </v-row>

            <v-row
              class="mb-4"
              dense
            >
              <v-col>
                <v-checkbox
                  v-for="type in schemaProperties.program_types.items.enum"
                  v-model="modelValue.program_types"
                  :key="type"
                  :label="capitalize($bct(type))"
                  :value="type"
                  class="c-black mt-0"
                  color="primary"
                  hide-details
                  multiple
                />
              </v-col>
            </v-row>

            <v-divider class="my-6" />
          </template>

          <template v-if="schemaProperties.county.filterable">
            <v-row dense>
              <v-col class="c-black fs-16 fw-600">
                {{ $bct(schemaProperties.county.alias || 'County') }}
              </v-col>
            </v-row>

            <v-row
              class="mb-4"
              dense
            >
              <v-col>
                <!-- eslint-disable-next-line prefer-standard-components/no-vuetify-inputs -- TODO migrate to LabeledSelect -->
                <v-select
                  v-model="modelValue.county"
                  :items="schemaProperties.county.enum"
                  :label="$bct('County')"
                  class="c-black mt-0"
                  color="primary"
                  hide-details
                  multiple
                />
              </v-col>
            </v-row>

            <v-divider class="my-6" />
          </template>

          <template v-if="schemaProperties.categories.filterable">
            <v-row dense>
              <v-col class="c-black fs-16 fw-600">
                {{ $bct(schemaProperties.categories.alias || 'Categories') }}
              </v-col>
            </v-row>

            <v-row
              class="mb-4"
              dense
            >
              <v-col>
                <template v-if="schemaProperties.categories.items.enum.length < 20">
                  <v-checkbox
                    v-for="type in schemaProperties.categories.items.enum"
                    v-model="modelValue.categories"
                    :key="type"
                    :label="capitalize($bct(type))"
                    :value="type"
                    class="c-black mt-0"
                    color="primary"
                    hide-details
                    multiple
                  />
                </template>

                <template v-else>
                  <v-autocomplete
                    v-model="modelValue.categories"
                    :items="schemaProperties.categories.items.enum"
                    :label="$bct('Select all that apply')"
                    variant="filled"
                    chips
                    closable-chips
                    hide-details
                    multiple
                  />
                </template>
              </v-col>
            </v-row>

            <v-divider class="my-6" />
          </template>

          <template v-if="schemaProperties.program_season.filterable">
            <v-row dense>
              <v-col class="c-black fs-16 fw-600">
                {{ $bct(schemaProperties.program_season.alias || 'Season') }}
              </v-col>
            </v-row>

            <v-row
              class="mb-4"
              dense
            >
              <v-col>
                <v-radio-group
                  v-model="modelValue.program_season"
                  class="mt-0"
                  hide-details
                >
                  <v-radio
                    v-for="type in schemaProperties.program_season.enum"
                    :key="type"
                    :label="capitalize($bct(type))"
                    :value="type"
                    class="c-black mt-0"
                    color="primary"
                    hide-details
                  />
                </v-radio-group>
              </v-col>
            </v-row>
            <v-divider class="my-6" />
          </template>

          <template v-if="schemaProperties.license_type.filterable">
            <v-row dense>
              <v-col class="c-black fs-16 fw-600">
                {{ $bct(schemaProperties.license_type.alias || 'License') }}
              </v-col>
            </v-row>

            <v-row
              class="mb-4"
              dense
            >
              <v-col>
                <v-checkbox
                  v-for="type in schemaProperties.license_type.enum"
                  v-model="modelValue.license_type"
                  :key="type"
                  :label="capitalize($bct(type))"
                  :value="type"
                  class="c-black mt-0"
                  color="primary"
                  hide-details
                  multiple
                />
              </v-col>
            </v-row>
            <v-divider class="my-6" />
          </template>

          <div v-if="schemaProperties.quality_rating_score.filterable">
            <v-row dense>
              <v-col class="c-black fs-16 fw-600">
                {{ $bct(schemaProperties.quality_rating_score.alias) }}
              </v-col>
            </v-row>

            <v-row dense>
              <v-col class="d-flex flex-column ga-2">
                <div
                  v-if="store.state.site.ratings.description || store.state.site.ratings.link"
                  class="d-flex align-center ga-2"
                >
                  <span>{{ $bct('Learn more') }}</span>

                  <TooltipIcon
                    v-if="store.state.site.ratings.description"
                    :text="store.state.site.ratings.description"
                    color="grey"
                    icon="info"
                    location="bottom"
                    open-on-click
                  />

                  <a
                    v-if="store.state.site.ratings.link"
                    :href="store.state.site.ratings.link"
                    class="c-primary fs-15 ms-1 underlined"
                    target="_blank"
                  >
                    <v-icon
                      icon="launch"
                      size="24"
                    />
                  </a>
                </div>

                <v-radio-group
                  v-model="modelValue.minimum_quality_rating"
                  hide-details
                >
                  <div
                    v-for="(level, index) in store.state.site.ratings.text.slice().reverse()"
                    :key="index"
                  >
                    <RadioWithContext
                      :index="index"
                      :item="level"
                      :suffix="
                        store.state.site.ratings.text.length - index <
                        store.state.site.ratings.text.length
                          ? $bct('or higher')
                          : null
                      "
                      :value="store.state.site.ratings.max - index"
                    />

                    <template v-if="level.subtitle">
                      <v-divider
                        v-show="index + 1 < store.state.site.ratings.text.length"
                        class="my-3"
                      />
                    </template>
                  </div>
                </v-radio-group>

                <v-btn
                  v-show="modelValue.minimum_quality_rating"
                  @click="modelValue.minimum_quality_rating = null"
                  color="primary"
                  size="small"
                >
                  {{ $bct('Clear filter') }}
                </v-btn>
              </v-col>
            </v-row>

            <v-divider class="my-6" />
          </div>

          <v-row>
            <v-col>
              <SearchMoreFilters :value="modelValue" />
            </v-col>
          </v-row>
        </v-card-text>

        <v-divider class="my-3" />

        <v-card-actions>
          <v-btn
            @click="clearAllFilters()"
            variant="text"
          >
            {{ $bct('Clear filters') }}
          </v-btn>

          <v-spacer />

          <v-btn
            @click="emitChange()"
            color="primary"
          >
            {{ $bct('Apply') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import _ from 'lodash';
import { useDisplay } from 'vuetify';
import { useStore } from 'vuex';

import { capitalize } from '@/plugins/filters';
import FilterMenu from '@/shared/components/form/FilterMenu.vue';
import LabeledOpenClose from '@/shared/components/form/LabeledOpenClose.vue';
import RadioWithContext from '@/shared/components/form/RadioWithContext.vue';
import SearchMoreFilters from '@/shared/components/search/MoreFilters.vue';
import TooltipIcon from '@/shared/components/TooltipIcon.vue';

const store = useStore();
const display = useDisplay();
const $bct = inject('translator');

const MAX_FILTERS_LOGGED_IN = 7;
const MAX_FILTERS_ANONYMOUS = 8;

const props = defineProps({
  hasFavorites: {
    type: Boolean,
    default: false,
  },
  hasReferrals: {
    type: Boolean,
    default: false,
  },
});

const MIN_WIDTH_WITH_REFERRALS = 1655;
const MIN_WIDTH_WITHOUT_REFERRALS = 1490;

const minWidthForOverflowFilters = computed(() =>
  props.hasReferrals ? MIN_WIDTH_WITH_REFERRALS : MIN_WIDTH_WITHOUT_REFERRALS,
);

const modelValue = defineModel({ type: Object });

const emit = defineEmits(['change', 'redraw']);

const allFiltersDialogIsVisible = ref(false);
const moreFiltersDialogIsVisible = ref(false);
const hoursRange = ref(
  modelValue.value.dropoff && modelValue.value.pickup
    ? [modelValue.value.dropoff, modelValue.value.pickup]
    : [900, 1600],
);
const meta = store.state.schemas[modelValue.value.schema_id].meta;

const moreFiltersActive = computed(() => {
  return (
    Object.values(modelValue.value.highlights).some((item) => item.length > 0) ||
    Object.values(modelValue.value.custom).some((item) => item && item.length > 0) ||
    modelValue.value.payment_subsidies_accepted.length > 0 ||
    !!modelValue.value.subsidy
  );
});

const moreFiltersButtonClass = computed(() => {
  let c = ['me-2'];
  if (moreFiltersActive.value) {
    c = c.concat(['bc-primary-light', 'bg-primary-extra-light']);
  } else {
    c = c.concat(['bg-white', 'bc-very-light-gray']);
  }
  return c.join(' ');
});

const schemaProperties = computed(() => {
  return store.state.schemas[modelValue.value.schema_id].definition.properties;
});

const filterCount = computed(() => {
  // Distance is not on Provider schema but enabled by Parent page > Search feature flag; it's an automatic filter
  // Because of this, doing count from 1
  let count = 1;
  if (meta.openings.enabled && meta.openings.public) count++;
  if (schemaProperties.value.quality_rating_score?.filterable) count++;
  if (schemaProperties.value.county?.filterable) count++;
  if (schemaProperties.value.program_types?.filterable) count++;
  if (schemaProperties.value.program_season?.filterable) count++;
  if (schemaProperties.value.license_type?.filterable) count++;
  if (schemaProperties.value.categories?.filterable) count++;
  if (schemaProperties.value.hours?.filterable) count++;
  if (store.state.pages.Search.features.enable_more_filters) count++;
  return count;
});

const showDesktopFilters = computed(() => {
  if (props.hasFavorites && filterCount.value > MAX_FILTERS_LOGGED_IN) {
    return display.width.value >= minWidthForOverflowFilters.value;
  }
  if (!props.hasFavorites && filterCount.value > MAX_FILTERS_ANONYMOUS) {
    return display.width.value >= minWidthForOverflowFilters.value;
  }
  return display.lgAndUp.value;
});

const overflowMode = computed(() => {
  // Only overflow for laptop size; XL screens see all filters and don't need overflow
  return props.hasFavorites && filterCount.value > MAX_FILTERS_LOGGED_IN && !display.xlAndUp.value;
});

const clearHours = () => {
  const newVal = modelValue.value;
  hoursRange.value = [900, 1600];
  newVal.dropoff = null;
  newVal.pickup = null;
  newVal.week_days = [];
  emit('change', newVal);
};

const clearAllFilters = () => {
  const newVal = modelValue.value;
  hoursRange.value = [900, 1600];
  newVal.vacancy = null;
  newVal.distance = null;
  newVal.dropoff = null;
  newVal.pickup = null;
  newVal.week_days = [];
  newVal.program_types = [];
  newVal.categories = [];
  newVal.license_type = [];
  newVal.minimum_quality_rating = null;
  newVal.program_season = null;
  newVal.highlights = {};
  newVal.custom = {};
  newVal.exclude_closed = null;
  newVal.payment_subsidies_accepted = [];
  newVal.payment_other_programs = [];
  newVal.subsidy = null;
  moreFiltersDialogIsVisible.value = false;
  emit('change', newVal);
};

const clearMoreFilters = () => {
  const newVal = modelValue.value;
  newVal.highlights = {};
  newVal.custom = {};
  newVal.exclude_closed = null;
  newVal.payment_subsidies_accepted = [];
  newVal.payment_other_programs = [];
  newVal.subsidy = null;
  newVal.week_days = [];
  moreFiltersDialogIsVisible.value = false;
  emit('change', newVal);
};

const openMoreFiltersDialog = () => {
  moreFiltersDialogIsVisible.value = true;
  emit('redraw');
};

const emitChange = () => {
  const newVal = modelValue.value;
  newVal.vacancy = newVal.vacancy || null;

  if (newVal.week_days.length > 0) {
    const [dropoff, pickup] = hoursRange.value;
    newVal.dropoff = dropoff;
    newVal.pickup = pickup;
  } else {
    newVal.dropoff = null;
    newVal.pickup = null;
  }

  allFiltersDialogIsVisible.value = false;
  moreFiltersDialogIsVisible.value = false;
  emit('change', newVal);
};

const mileageRange = () => {
  return _.range(1, 31);
};
</script>
